import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Table, Form, Button } from 'react-bootstrap';
import { toastr } from 'react-redux-toastr';

import Loader from '../../../components/Common/Loader/Loader';
import getAllSectionsQuery from './getAllSections.graphql';
import manageSectionMutation from './manageSection.graphql';
import deleteSectionMutation from './deleteSection.graphql';
import reorderSectionsMutation from './reorderSections.graphql';
import getCategoriesForAssignQuery from './getCategoriesForAssign.graphql';
import assignCategorySectionMutation from './assignCategorySection.graphql';
import { api, categoryUploadDir } from '../../../config';

// Admin: Manage Sections — the big category groupings (Home Improvement, Mounting, Cleaning, ...).
// Create / rename / activate / reorder / delete. Assigning a service to a section is done on the
// category's own edit page (Section dropdown). Deleting a section never deletes its services.
const Sections = () => {
  const { loading, data, refetch } = useQuery(getAllSectionsQuery, { fetchPolicy: 'network-only' });
  const [manage, { loading: saving }] = useMutation(manageSectionMutation);
  const [remove] = useMutation(deleteSectionMutation);
  const [reorder] = useMutation(reorderSectionsMutation);
  const { data: catData, refetch: refetchCats } = useQuery(getCategoriesForAssignQuery, { fetchPolicy: 'network-only' });
  const [assign] = useMutation(assignCategorySectionMutation);

  const sections = data?.getAllSections?.result || [];
  const categories = catData?.getActiveCategories?.results || [];

  const onAssign = async (categoryId, sectionId) => {
    try {
      const res = await assign({ variables: { categoryId, sectionId: sectionId || null } });
      if (res?.data?.assignCategorySection?.status === 200) {
        toastr.success('Assigned', 'Service moved.');
        await refetchCats();
      } else toastr.error('Error', res?.data?.assignCategorySection?.errorMessage || 'Could not assign.');
    } catch (e) { toastr.error('Error', e.message || 'Could not assign.'); }
  };
  const [edits, setEdits] = useState({}); // id -> { name, status }
  const [newName, setNewName] = useState('');

  const nameVal = (s) => (edits[s.id]?.name !== undefined ? edits[s.id].name : (s.name || ''));
  const statusVal = (s) => (edits[s.id]?.status !== undefined ? edits[s.id].status : (s.status || 'active'));
  const setEdit = (id, field, value) => setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const saveRow = async (s) => {
    const name = nameVal(s).trim();
    if (!name) { toastr.error('Error', 'Section name is required.'); return; }
    try {
      const res = await manage({ variables: { id: s.id, name, status: statusVal(s) } });
      const out = res?.data?.manageSection;
      if (out?.status === 200) {
        toastr.success('Saved', `${name} updated.`);
        setEdits((prev) => { const n = { ...prev }; delete n[s.id]; return n; });
        await refetch();
      } else toastr.error('Error', out?.errorMessage || 'Could not save.');
    } catch (e) { toastr.error('Error', e.message || 'Could not save.'); }
  };

  const addSection = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const res = await manage({ variables: { name } });
      const out = res?.data?.manageSection;
      if (out?.status === 200) {
        toastr.success('Added', `${name} created.`);
        setNewName('');
        await refetch();
      } else toastr.error('Error', out?.errorMessage || 'Could not add.');
    } catch (e) { toastr.error('Error', e.message || 'Could not add.'); }
  };

  const onDelete = async (s) => {
    if (typeof window !== 'undefined' &&
        !window.confirm(`Delete section "${s.name}"? Its services become unassigned (they are NOT deleted).`)) return;
    try {
      const res = await remove({ variables: { id: s.id } });
      const out = res?.data?.deleteSection;
      if (out?.status === 200) {
        toastr.success('Deleted', `${s.name} removed.`);
        await refetch();
      } else toastr.error('Error', out?.errorMessage || 'Could not delete.');
    } catch (e) { toastr.error('Error', e.message || 'Could not delete.'); }
  };

  const move = async (index, dir) => {
    const ids = sections.map((s) => s.id);
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    const tmp = ids[index]; ids[index] = ids[j]; ids[j] = tmp;
    try {
      const res = await reorder({ variables: { orderedIds: ids } });
      if (res?.data?.reorderSections?.status === 200) await refetch();
      else toastr.error('Error', 'Could not reorder.');
    } catch (e) { toastr.error('Error', e.message || 'Could not reorder.'); }
  };

  const iconUrl = (icon) => (icon ? (api?.apiEndpoint || '') + categoryUploadDir + icon : null);

  const uploadIcon = async (s, file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('image', file);
      const resp = await fetch('/uploadSectionIcon', { method: 'POST', body: fd });
      const json = await resp.json();
      if (json?.status === 200 && json.fileName) {
        const res = await manage({ variables: { id: s.id, name: s.name, status: s.status, icon: json.fileName } });
        if (res?.data?.manageSection?.status === 200) {
          toastr.success('Icon updated', s.name);
          await refetch();
        } else toastr.error('Error', res?.data?.manageSection?.errorMessage || 'Could not save icon.');
      } else toastr.error('Error', json?.errorMessage || 'Upload failed.');
    } catch (e) { toastr.error('Error', e.message || 'Upload failed.'); }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 className="siteAdminPrimaryHeading">Manage Sections</h1>
      <p style={{ color: '#5f6368', maxWidth: 760 }}>
        Sections are the big groups customers and pros browse — Home Improvement &amp; Repairs, Mounting
        &amp; Installation, Cleaning, and so on. Reorder them with the arrows. Assign a service to a
        section from that category&apos;s edit page (Section dropdown). Deleting a section never deletes
        its services — they simply become unassigned.
      </p>

      <div style={{ display: 'flex', gap: 8, margin: '16px 0', maxWidth: 480 }}>
        <Form.Control
          placeholder="New section name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <Button onClick={addSection} disabled={saving || !newName.trim()}>Add</Button>
      </div>

      <Loader type="page" show={loading}>
        {!loading && (
          <Table responsive bordered hover style={{ background: '#fff' }}>
            <thead>
              <tr>
                <th style={{ width: 100 }}>Order</th>
                <th style={{ width: 90 }}>Icon</th>
                <th>Name</th>
                <th style={{ width: 150 }}>Status</th>
                <th style={{ width: 170 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((s, i) => (
                <tr key={s.id}>
                  <td>
                    <Button size="sm" variant="light" disabled={i === 0} onClick={() => move(i, -1)}>↑</Button>{' '}
                    <Button size="sm" variant="light" disabled={i === sections.length - 1} onClick={() => move(i, 1)}>↓</Button>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {s.icon
                      ? <img src={iconUrl(s.icon)} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} />
                      : <span style={{ color: '#bbb' }}>—</span>}
                    <label style={{ display: 'block', fontSize: 11, color: '#1a73e8', cursor: 'pointer', marginTop: 2 }}>
                      Upload
                      <input type="file" accept="image/*" style={{ display: 'none' }}
                        onChange={(e) => uploadIcon(s, e.target.files && e.target.files[0])} />
                    </label>
                  </td>
                  <td>
                    <Form.Control value={nameVal(s)} onChange={(e) => setEdit(s.id, 'name', e.target.value)} />
                  </td>
                  <td>
                    <select
                      className="form-control"
                      value={statusVal(s)}
                      onChange={(e) => setEdit(s.id, 'status', e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </td>
                  <td>
                    <Button size="sm" onClick={() => saveRow(s)} disabled={saving}>Save</Button>{' '}
                    <Button size="sm" variant="danger" onClick={() => onDelete(s)}>Delete</Button>
                  </td>
                </tr>
              ))}
              {sections.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#9aa0a6' }}>No sections yet.</td></tr>
              )}
            </tbody>
          </Table>
        )}
      </Loader>

      <h2 style={{ marginTop: 36, fontSize: 20 }}>Assign services to sections</h2>
      <p style={{ color: '#5f6368', maxWidth: 760 }}>
        Pick the section each service belongs to. Changes save immediately. &quot;Unassigned&quot; hides
        the service from the grouped browse until you give it a section.
      </p>
      <Table responsive bordered hover style={{ background: '#fff' }}>
        <thead>
          <tr><th>Service</th><th style={{ width: 280 }}>Section</th></tr>
        </thead>
        <tbody>
          {categories.map((c) => (
            <tr key={c.id}>
              <td>{c.name}</td>
              <td>
                <select
                  className="form-control"
                  value={c.sectionId || ''}
                  onChange={(e) => onAssign(c.id, e.target.value ? parseInt(e.target.value, 10) : null)}
                >
                  <option value="">— Unassigned —</option>
                  {sections.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
          {categories.length === 0 && (
            <tr><td colSpan={2} style={{ textAlign: 'center', color: '#9aa0a6' }}>No services.</td></tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default Sections;
